import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { marker } from '@colsen1991/ngx-translate-extract-marker';

import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { CompanyCodeDataService, ICompanyCode } from '@lib/company-code';
import { DemandDataService } from '../../../demand/demand-data.service';
import { IDemandDraftFromPdfOutcome } from '../../../demand/types/IDemandDraftFromPdfOutcome';

const errorText = marker('PDF-Analyse fehlgeschlagen.');
const draftAvailableInfoText = marker(
    'Die Analyse läuft weiter. Eine daraus entstandene Bedarfsmeldung ist als Entwurf verfügbar und kann über den Button „Neuen Bedarf melden" geöffnet werden.',
);

/** Payload of an AMBIGUOUS outcome, handed to the supplier-selection dialog by the caller. */
export interface PurchaseRequisitionPdfUploadAmbiguousResult {
    file: File;
    companyCodeId: string;
    outcome: IDemandDraftFromPdfOutcome;
}

export class PurchaseRequisitionPdfUploadDialogResult {
    constructor(
        public readonly created: boolean,
        public readonly demandId?: string,
        public readonly ambiguous?: PurchaseRequisitionPdfUploadAmbiguousResult,
    ) {}
}

@Component({
    selector: 'purchase-requisition-pdf-upload-dialog',
    templateUrl: './purchase-requisition-pdf-upload-dialog.component.html',
    styleUrls: ['./purchase-requisition-pdf-upload-dialog.component.scss'],
    standalone: false,
})
export class PurchaseRequisitionPdfUploadDialogComponent {
    private readonly notificationService = inject(NotificationService);
    private readonly demandDataService = inject(DemandDataService);
    private readonly companyCodeDataService = inject(CompanyCodeDataService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialogRef = inject<
        MatDialogRef<PurchaseRequisitionPdfUploadDialogComponent, PurchaseRequisitionPdfUploadDialogResult>
    >(MatDialogRef);

    readonly droppedFile = signal<File | undefined>(undefined);
    readonly uploadError = signal<string | undefined>(undefined);
    readonly isUploading = signal(false);

    /** Company codes the user may pick; a supplier can only be auto-assigned once one is chosen. */
    readonly companyCodes = signal<ICompanyCode[]>([]);
    readonly selectedCompanyCodeId = signal<string | undefined>(undefined);
    readonly companyCodesLoading = signal(true);
    readonly companyCodesError = signal(false);

    readonly sortedCompanyCodes = computed(() =>
        [...this.companyCodes()].sort((left, right) => left.code.localeCompare(right.code)),
    );

    /** Only render the dropdown when there is an actual choice (more than one company code). */
    readonly showCompanyCodeSelect = computed(() => this.companyCodes().length > 1);
    /**
     * Upload is blocked while company codes are still loading or failed to load, and — when the user
     * has any company codes — until one is selected. A user with no company codes may still upload
     * (no company code is sent and no supplier is auto-assigned).
     */
    readonly uploadDisabled = computed(
        () =>
            !this.droppedFile() ||
            this.isUploading() ||
            this.companyCodesLoading() ||
            this.companyCodesError() ||
            (this.companyCodes().length > 0 && !this.selectedCompanyCodeId()),
    );

    /** True while another PDF analysis is still running; a new analysis would overwrite it. */
    readonly analysisRunning = this.demandDataService.pdfAnalysisRunning;
    /** True if an unannounced demand draft already exists; a new analysis would overwrite it. */
    readonly draftExists = signal(false);

    /** Set when the user closed the dialog while the analysis was still running. */
    private cancelled = false;

    constructor() {
        this.demandDataService
            .hasUnannouncedDemand()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((exists: boolean) => this.draftExists.set(exists));

        this.companyCodeDataService
            .getMyActive()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (companyCodes: ICompanyCode[]) => {
                    this.companyCodes.set(companyCodes);
                    this.companyCodesLoading.set(false);
                    if (companyCodes.length === 1) {
                        this.selectedCompanyCodeId.set(companyCodes[0].id);
                    }
                },
                error: () => {
                    this.companyCodesError.set(true);
                    this.companyCodesLoading.set(false);
                },
            });
    }

    onCompanyCodeSelected(event: Event): void {
        const value = (event.target as HTMLSelectElement).value;
        this.selectedCompanyCodeId.set(value || undefined);
    }

    onPdfDropped(event: NgxDropzoneChangeEvent): void {
        if (this.isUploading()) {
            return;
        }
        this.droppedFile.set(event.addedFiles[0]);
        this.uploadError.set(undefined);
    }

    removeDroppedFile(): void {
        this.droppedFile.set(undefined);
    }

    /**
     * Runs the analysis while the dialog stays open (no fire-and-forget notification anymore —
     * EPROC-11092): a RESOLVED outcome navigates straight to the created draft, an AMBIGUOUS one
     * hands file + candidates to the caller, which opens the supplier-selection dialog.
     */
    uploadPdf(): void {
        const file = this.droppedFile();
        const companyCodeId = this.selectedCompanyCodeId();
        if (!file || this.uploadDisabled()) {
            return;
        }

        this.isUploading.set(true);
        this.uploadError.set(undefined);
        // Backdrop click / ESC would bypass close() and drop the running analysis silently;
        // the explicit cancel button (and header X) keep working through close().
        this.dialogRef.disableClose = true;

        // Deliberately not bound to the component lifecycle: closing the dialog while the request
        // is running must not abort the analysis (see close()).
        this.demandDataService.analyzePdfToDraft(file, companyCodeId).subscribe({
            next: (outcome: IDemandDraftFromPdfOutcome) => {
                if (this.cancelled) {
                    return;
                }
                if (outcome.status === 'AMBIGUOUS') {
                    if (!companyCodeId) {
                        // Server-side impossible today (ambiguity needs a company code); guard
                        // against silently doing nothing should that invariant ever change.
                        this.isUploading.set(false);
                        this.dialogRef.disableClose = false;
                        this.uploadError.set(errorText);
                        return;
                    }
                    this.dialogRef.close(
                        new PurchaseRequisitionPdfUploadDialogResult(false, undefined, {
                            file,
                            companyCodeId,
                            outcome,
                        }),
                    );
                    return;
                }
                // Navigation to the created draft happens in the caller (the list component).
                this.dialogRef.close(new PurchaseRequisitionPdfUploadDialogResult(true, outcome.demandId));
            },
            error: (errorResponse) => {
                if (this.cancelled) {
                    return;
                }
                this.isUploading.set(false);
                this.dialogRef.disableClose = false;
                const message = typeof errorResponse?.error === 'string' ? errorResponse.error : undefined;
                this.uploadError.set(message || errorText);
            },
        });
    }

    close(): void {
        if (this.isUploading()) {
            // The analysis keeps running server-side; a draft it creates stays reachable as the
            // unannounced draft. Only the navigation/result handling is dropped.
            this.cancelled = true;
            this.notificationService.showNotification(draftAvailableInfoText, NotificationMessageLevel.INFO);
        }
        this.dialogRef.close();
    }
}
